import React, { createRef, useCallback, useEffect, useState } from "react";
import cloneDeep from "lodash/cloneDeep";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import {
  getSteps,
  resetApp,
  setActiveStream,
  setChartDots,
  setDatasetAction,
  setOrderLimits,
  setRandomOrders,
  setSmoothArrayAction,
  setTick,
  setTickDuration
} from "../../entities/actions";
import { selectorGetSimulationSpeed } from "../../entities";
import { selectorGetDataset } from "../../entities/selectors";
import { Ranking } from "../Ranking";
import { Timetable } from "../Timetable";
import Streams from "../Streams";
import { Orders } from "../Orders";
import "./styles.css";
import { api } from "../../services/api/const";
import { orderTypeEnum } from "../consts";
import { ScenarioGrid } from "../ScenarioGrid/ScenarioGrid";
import { Header } from "../Header";
import { OrdersPlaceholder } from "../Orders/OrdersPlaceholder";
import { getRandomLimit } from "../../utils/getRandomLimit";
import { getRandomTypeAndRate } from "../../utils/getRandomTypeAndRate";

export const rnd = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

export const MainPage = ({ setKey }) => {
  const dispatch = useDispatch();
  const ref = createRef(null);

  const dataset = useSelector(selectorGetDataset);
  const simulationSpeed = useSelector(selectorGetSimulationSpeed);
  const screenData = useSelector(state => state.init.screenData);
  const state = useSelector(state => state.init);
  const currentTick = useSelector(state => state.init.currentTick);
  const chartArray = useSelector(state => state.init.smoothArray);
  const orders = useSelector(state => state.init.orders);
  const limits = useSelector(state => state.init.limits);
  const pointStep = useSelector(state => state.init.pointStep);
  const stepsAll = useSelector(state => state.init.steps);
  const stream_executions = useSelector(state => state.init.stream_executions);
  const setDatasetToStore = useCallback(
    payload => dispatch(setDatasetAction(payload)),
    [dispatch]
  );
  const setTickDurationToStore = useCallback(
    payload => dispatch(setTickDuration(payload)),
    [dispatch]
  );
  const setSmoothArrayToStore = useCallback(
    payload => dispatch(setSmoothArrayAction(payload)),
    [dispatch]
  );
  const [dots, setDots] = useState([]);
  const [minValue, setMin] = useState(null);
  const [maxValue, setMax] = useState(null);
  const [scale, setScale] = useState(null);
  const [start, setStart] = useState(false);
  const [simulation, startSimulation] = useState(false);
  const [tick, setTickState] = useState(0);
  const [delay, setDelay] = useState(1000);
  const [tint, setTint] = useState(true);
  const [dSet, setDataset] = useState("DS101-N");
  const chartDots = screenData || [];
  const currentRate = chartArray[currentTick] ?? chartArray[0];
  const isSimulationFinished =
    currentTick === chartArray.length - 1 && chartArray.length;
  const [longPressInterval, setLongPressInterval] = useState(null);
  const [shouldBlockScenario, setShouldBlockScenario] = useState(false);

  const [simulators, setSimulators] = useState(null);
  const [simulator, setSimulator] = useState(null);

  const generateSteppedYArray = (min = 10, max = 500, step = 10) => {
    const result = [5];
    const steps = Array.from(
      { length: (max - min) / step + 1 },
      (_, i) => min + i * step
    );
    result.push(...steps);
    return result;
  };

  const getRandomOrders = (lowerLimit, upperLimit) => {
    const stratEnum = ["15%", "30%", "200%", "LS"];
    const types = [orderTypeEnum.xy, orderTypeEnum.lsb, orderTypeEnum.lsh];

    const orders = [];
    for (let i = 0; i < 5; i += 1) {
      const size = rnd(5, 50) * 1000;
      const stratEdge = orders.find(e => e.strat === "LS") ? 2 : 3;
      const strat = stratEnum[rnd(0, stratEdge)];

      orders.push({
        size,
        strat,
        streamRate: {
          x: undefined,
          y: undefined
        }
      });
    }
    return orders;
  };

  const activeOrders = stepsAll[currentTick]?.streams
    .filter(el => !el.is_done)
    .map(e => ({ sid: e.stream_id, orders: Object.values(e.orders_by_side) }))
    .reduce((acc, el) => {
      acc[el.orders[0]] = {
        ...acc[el.orders[0]],
        [el.sid]: el.orders[1].slice(3)
      };
      acc[el.orders[1]] = {
        ...acc[el.orders[1]],
        [el.sid]: el.orders[0].slice(3)
      };
      return acc;
    }, {});

  const initData = async defaultSimulator => {
    setShouldBlockScenario(false);
    const rnd = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;
    const ds = `DS10${rnd(1, 8)}-N`;
    setDataset(ds);

    try {
      var result = await axios(
        `${api}/api/v1/simulators/${defaultSimulator.id}/random-dataset/`
      );
    } catch (error) {
      console.log("error", error);
    }
    const lower = result?.data?.MIN_PRICE;
    const upper = result?.data?.MAX_PRICE;

    setDatasetToStore(result?.data.DATA.slice(0));
    dispatch(setRandomOrders(getRandomOrders(lower, upper)));
    dispatch(setOrderLimits({ lower, upper }));
    setTint(false);
  };

  const handleSetSimulator = async simulator => {
    setSimulator(simulator);
    await resetSimulation(simulator);
  };

  useEffect(async () => {
    const simulators = await axios(`${api}/api/v1/simulators/`);
    setSimulators(simulators.data);
    const defaultSimulator = simulators.data.find(e => e.is_default);
    setSimulator(defaultSimulator);

    await initData(defaultSimulator);
  }, []);

  const randomize = (lowerLimit, upperLimit) => {
    const rnd = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

    const rndOrders = orders.map((e, i, arr) => {
      const xPos = (i + 1) * 80;

      return {
        ...e,
        x: xPos,
        created_at_tp: Math.round(xPos / pointStep)
      };
    });

    const getPreparedOrders = rndOrders => {
      const sides = ["BUY", "SELL"];
      const assignedOrders = cloneDeep(rndOrders);

      // Get two random unique indices
      const firstIndex = Math.floor(Math.random() * assignedOrders.length);
      let secondIndex;
      do {
        secondIndex = Math.floor(Math.random() * assignedOrders.length);
      } while (secondIndex === firstIndex); // Ensure indices are different

      // Assign one as "BUY" and one as "SELL"
      assignedOrders[firstIndex].side = "BUY";
      assignedOrders[secondIndex].side = "SELL";

      // Assign other orders
      for (let i = 0; i < assignedOrders.length; i++) {
        if (i !== firstIndex && i !== secondIndex) {
          assignedOrders[i].side = sides[rnd(0, 1)];
        }
        assignedOrders[i].row =
          assignedOrders[i].side === "BUY" ? "line1" : "line4";
        assignedOrders[i].limit = getRandomLimit({
          limits: { lower: lowerLimit, upper: upperLimit },
          sideValue: assignedOrders[i].side
        });
        const { type, streamRate } = getRandomTypeAndRate({
          orders: assignedOrders
        });
        assignedOrders[i].streamRate = streamRate;
        assignedOrders[i].type = type;
      }
      return assignedOrders;
    };

    const preparedOrders = getPreparedOrders(rndOrders);
    dispatch(setRandomOrders(preparedOrders));
  };

  useEffect(() => {
    let interval;
    if (start && stepsAll?.length > 0) {
      interval = setInterval(() => {
        if (tick < chartArray?.length - 1) {
          setTickState(tick =>
            tick < chartArray?.length - 1 ? tick + 1 : tick
          );
        } else {
          setStart(false);
        }
      }, delay);
    }
    return () => clearInterval(interval);
  }, [start, stepsAll?.length]);

  useEffect(() => {
    dispatch(setTick(tick));
    if (tick === chartArray?.length - 1) {
      setStart(false);
    }
  }, [tick]);

  useEffect(() => {
    if (simulation) {
      setTint(true);
      const ordersState = orders
        .filter(e => e.x)
        .map(e => {
          const { side, size, limit, created_at_tp, type, streamRate } = e;

          const updatedStreamRate =
            streamRate.x === undefined && streamRate.y === undefined
              ? null
              : { x: streamRate.x ?? null, y: streamRate.y ?? null };

          return {
            event_type: "ON",
            quantity: size,
            side,
            limit: limit ? limit.toFixed(2) : null,
            created_at_tp,
            type,
            stream_rate: updatedStreamRate
          };
        });

      const body = cloneDeep(chartArray);
      ordersState.forEach((order, i) =>
        body.splice(order.created_at_tp + i + 1, 0, order)
      );
      const data = body.map(e => {
        const { created_at_tp, is_reference, ...tmp } = e;
        return { ...tmp, is_reference: is_reference };
      });
      setShouldBlockScenario(true);
      dispatch(getSteps(data));
    }
  }, [simulation]);

  useEffect(() => {
    if (stepsAll?.length) {
      setTint(false);
    }
  }, [stepsAll?.length]);

  const startStop = () => {
    const activeOrders = orders.filter(e => e.x);
    if (
      activeOrders.length < 2 ||
      !activeOrders.some(e => e.side === "BUY") ||
      !activeOrders.some(e => e.side === "SELL")
    )
      return;
    if (!start) {
      setStart(true);
      startSimulation(true);
    }
    if (start) setStart(false);
  };

  const resetSimulation = async simulator => {
    setStart(false);
    setTickState(0);
    startSimulation(false);
    dispatch(resetApp());
    setTint(true);
    const defaultSimulator = simulators.find(e => e.is_default);
    await initData(simulator ?? defaultSimulator);
  };

  useEffect(() => {
    const getMinValue = () => {
      let minValue = chartArray[0]?.price;

      chartArray?.forEach(item => {
        if (minValue > item.price) {
          minValue = item.price;
        }
      });
      return minValue;
    };

    const getMaxValue = () => {
      let maxValue = chartArray[0]?.price;

      chartArray?.forEach(item => {
        if (maxValue < item.price) {
          maxValue = item.price;
        }
      });
      return maxValue;
    };
    if (chartArray?.length) {
      setMin(getMinValue());
      setMax(getMaxValue());
      setDelay(30000 / chartArray?.length);
    }
  }, [chartArray]);

  useEffect(() => {
    const getScale = () => {
      const shift = (limits.upper - limits.lower) / 2;
      const scale = Math.round(
        230 / ((limits.upper + shift - (limits.lower - shift)) * 100)
      );
      return scale;
    };
    if (minValue !== null && maxValue !== null) {
      setScale(getScale());
    }
  }, [minValue, maxValue]);

  useEffect(() => {
    if (dataset?.length) {
      const chartArray = dataset.slice(
        0,
        Math.round(dataset?.length / 10) * simulationSpeed
      );
      setTickDurationToStore(
        Math.round(30000 / ((dataset?.length / 100) * simulationSpeed * 10))
      );
      setSmoothArrayToStore(chartArray);
    }
  }, [dataset?.length, simulationSpeed]);

  useEffect(() => {
    if (!limits?.upper || !limits.lower) return;
    const shift = (limits.upper - limits.lower) / 2;
    const chartDots = chartArray
      .map((e, i) => [
        i * (860 / (chartArray?.length - 1)),
        limits.upper + shift - chartArray[i].price < 0
          ? 0
          : chartArray[i].price < limits.lower - shift
          ? 230
          : (limits.upper + shift - chartArray[i].price) * 100 * scale
      ])
      .flat();
    dispatch(setChartDots(chartDots));
  }, [limits, scale, chartArray]);

  useEffect(() => {
    setDots(chartDots.slice(0, (currentTick + 1) * 2));
  }, [currentTick]);

  const rewindMouseDown = direction => {
    const interval = setInterval(() => {
      // if it was a long press, rewind
      if (direction === "forward") {
        rewindForward();
      } else {
        rewindBack();
      }
    }, 200);
    setLongPressInterval(interval);
  };

  const rewindMouseUp = direction => {
    // if it was a short press, step
    if (direction === "forward") {
      stepForward();
    } else {
      stepBack();
    }
    clearInterval(longPressInterval);
    setLongPressInterval(null);
  };

  const rewindMouseLeave = () => {
    clearInterval(longPressInterval);
    setLongPressInterval(null);
  };

  const stepBack = () => {
    if (currentTick > 0 && !start) {
      setTickState(tick => tick - 1);
    }
  };

  const stepForward = () => {
    if (currentTick < chartArray?.length - 1 && !start) {
      setTickState(tick => tick + 1);
    }
  };

  const rewindBack = () => {
    setTickState(tick => {
      const newTick = tick - 10;
      if (newTick <= 0) {
        return 0;
      } else {
        return newTick;
      }
    });
  };

  const rewindForward = () => {
    setTickState(tick => {
      const newTick = tick + 10;
      if (newTick >= chartArray?.length - 1) {
        return chartArray?.length - 1;
      } else {
        return newTick;
      }
    });
  };

  const jumpToEnd = () => {
    setTickState(chartArray?.length - 1);
  };

  const jumpToStart = () => {
    setTickState(-1);
  };

  const randomizeOrders = () => {
    randomize(limits.lower, limits.upper);
  };

  const reset = () => {
    setKey(Math.random());
    dispatch(resetApp());
  };

  const isOrdersNull = order => {
    return order.x === null;
  };

  const replay = () => {
    setStart(false);
    setTickState(0);
    startSimulation(false);
    const chartArray = dataset.slice(
      0,
      Math.round(dataset?.length / 10) * simulationSpeed
    );
    setTickDurationToStore(
      Math.round(30000 / ((dataset?.length / 100) * simulationSpeed * 10))
    );
    setSmoothArrayToStore(chartArray);
    dispatch({ type: "RESET_STEPS" });
    dispatch(setActiveStream(null));
  };

  return (
    <>
      <Header
        selectedSimulator={simulator}
        simulators={simulators}
        handleSetSimulator={handleSetSimulator}
      />
      <div className="body" ref={ref}>
        {tint && (
          <div className="tint-cover">
            <div className="lds-spinner">
              <div></div>
              <div></div>
              <div></div>
              <div></div>
              <div></div>
              <div></div>
              <div></div>
              <div></div>
              <div></div>
              <div></div>
              <div></div>
              <div></div>
            </div>
          </div>
        )}
        <div className="wrapper">
          <div className="ranking-and-timetable">
            <ScenarioGrid
              orders={orders}
              simulation={simulation}
              limits={limits}
              shouldBlockScenario={shouldBlockScenario}
            />
            <Timetable
              simulator={simulator}
              chartArray={chartArray}
              start={start}
              simulationSpeed={simulationSpeed}
              chartDots={dots}
              simulation={simulation}
              currentTick={currentTick}
              steps={stepsAll}
              randomize={randomizeOrders}
              stepBack={stepBack}
              stepForward={stepForward}
              rewindMouseDown={rewindMouseDown}
              rewindMouseUp={rewindMouseUp}
              rewindMouseLeave={rewindMouseLeave}
              startStop={startStop}
              resetSimulation={resetSimulation}
              maxMin={{ min: minValue, max: maxValue }}
              activeOrders={activeOrders}
              jumpToEnd={jumpToEnd}
              jumpToStart={jumpToStart}
            />
            <Ranking currentTick={currentTick} steps={stepsAll} />
          </div>
          <Streams
            nbbo={[currentRate?.bid || "--", currentRate?.offer || "--"]}
            start={start}
            elQty={chartArray?.length}
            delay={delay}
            currentTick={currentTick}
            steps={stepsAll}
            simulation={simulation}
            stream_executions={stream_executions}
            chartArray={chartArray}
            startStop={startStop}
            orders={orders}
          />
          {!orders.every(isOrdersNull) ? (
            <Orders
              currentTick={currentTick}
              steps={stepsAll}
              orders={orders}
              simulation={simulation}
            />
          ) : (
            <OrdersPlaceholder />
          )}
        </div>
      </div>
      <footer>
        <div className="footer-top">
          <span></span>
        </div>
        <div className="footer-bottom">
          <span>
            Disclaimer: The simulations generated are for instructional purposes
            for institutional traders only, and actual trading results may vary
            from simulated results.
          </span>
        </div>
      </footer>
      <div className="resolution-info">
        You need to use screen resolution more than 1280x768
      </div>
    </>
  );
};
